import React, { useEffect, useState } from "react";
import {
  FaUserMd,
  FaMicroscope,
  FaPills,
  FaHeart,
  FaUsers,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaPlay,
  FaMobileAlt,
  FaComments,
  FaExchangeAlt,
} from "react-icons/fa";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import DownloadModal from "../../components/DownloadModal/DownloadModal";
import styles from "./Features.module.css";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import { ROUTES } from "../../constants/routes";

const FeaturesPage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    // Scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1 }
    );

    document
      .querySelectorAll(".scroll-reveal, .scroll-reveal-scale")
      .forEach((el) => {
        observer.observe(el);
      });

    return () => observer.disconnect();
  }, []);

  const appFeatures = [
    {
      icon: <FaUserMd />,
      title: "Doctor Consultations",
      description:
        "Interactive chat and video sessions with certified doctors who control the flow",
      stats: "800+ Doctors",
      gradient: "blue",
    },
    {
      icon: <FaMicroscope />,
      title: "Lab Integration",
      description:
        "Seamless referrals to labs with shared medical information and instant results",
      stats: "500+ Tests",
      gradient: "green",
    },
    {
      icon: <FaPills />,
      title: "Pharmacy Network",
      description:
        "Direct prescription fulfillment through verified merchant pharmacies",
      stats: "2,000+ Pharmacies",
      gradient: "orange",
    },
    {
      icon: <FaHeart />,
      title: "Fee Cashback System",
      description:
        "Earn up to 50% cashback on transaction fees - withdraw, donate, or use for payments",
      stats: "Up to 50% Back",
      gradient: "red",
    },
  ];

  const mobileCapabilities = [
    {
      icon: <FaComments />,
      title: "Doctor-Controlled Sessions",
      description:
        "Doctors manage chat sessions and can suspend or end consultations as needed",
    },
    {
      icon: <FaExchangeAlt />,
      title: "Smart Referral System",
      description:
        "Doctors can refer patients to specialists or share data with labs seamlessly",
    },
    {
      icon: <FaShieldAlt />,
      title: "Virtual Bank Accounts",
      description:
        "Dedicated virtual accounts for secure transactions and fee cashback management",
    },
    {
      icon: <FaUsers />,
      title: "Merchant Network",
      description:
        "Connected ecosystem where doctors, labs, and pharmacies work together",
    },
    {
      icon: <FaChartLine />,
      title: "Flexible Withdrawals",
      description:
        "Merchants can add up to 3 withdrawal accounts with admin-controlled processing",
    },
    {
      icon: <FaClock />,
      title: "Configurable Thresholds",
      description:
        "Admin-controlled minimum fee cashback thresholds and withdrawal frequencies",
    },
  ];

  const platformStats = [
    { number: "20K+", label: "App Users", icon: <FaUsers /> },
    { number: "800+", label: "Healthcare Providers", icon: <FaUserMd /> },
    { number: "36", label: "States Covered", icon: <FaMapMarkerAlt /> },
    { number: "24/7", label: "Mobile Support", icon: <FaClock /> },
  ];

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div
        className={`${styles.featuresPage} ${isVisible ? styles.fadeIn : ""}`}
      >
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>
                <FaMobileAlt className={styles.badgeIcon} />
                Mobile App Features
              </div>

              <h1 className={styles.heroTitle}>
                Complete Healthcare
                <span className={styles.heroAccent}> Ecosystem</span>
              </h1>

              <p className={styles.heroSubtitle}>
                All healthcare services happen in our mobile app. Patients
                connect with doctors, labs, and pharmacies in one seamless
                platform with transparent fee cashback rewards and flexible
                payment options.
              </p>

              <div className={styles.heroActions}>
                <DownloadModal variant="button" className={styles.btnPrimary} />
                <a href="#features" className={styles.btnOutline}>
                  <FaPlay className={styles.btnIcon} />
                  Explore App Features
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className={styles.statsSection}>
          <div className={styles.container}>
            <div className={styles.statsGrid}>
              {platformStats.map((stat, index) => (
                <div
                  key={index}
                  className={`${styles.statCard} scroll-reveal-scale`}
                >
                  <div className={styles.statIcon}>{stat.icon}</div>
                  <div className={styles.statNumber}>{stat.number}</div>
                  <div className={styles.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile App Features */}
        <section className={styles.coreFeatures} id="features">
          <div className={styles.container}>
            <div className={`${styles.sectionHeader} scroll-reveal`}>
              <h2 className={styles.sectionTitle}>
                Mobile{" "}
                <span className={styles.textGradient}>Healthcare Platform</span>
              </h2>
              <p className={styles.sectionSubtitle}>
                Everything happens in our mobile app - where patients, doctors,
                labs, and pharmacies connect withcost structures and cashback rewards
              </p>
            </div>

            <div className={styles.coreGrid}>
              {appFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`${styles.coreCard} ${
                    styles[`gradient${feature.gradient}`]
                  } scroll-reveal`}
                >
                  <div className={styles.cardIcon}>{feature.icon}</div>
                  <div className={styles.cardContent}>
                    <div className={styles.cardHeader}>
                      <h3 className={styles.cardTitle}>{feature.title}</h3>
                      <span className={styles.cardStats}>{feature.stats}</span>
                    </div>
                    <p className={styles.cardDescription}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Capabilities */}
        <section className={styles.platformFeatures}>
          <div className={styles.container}>
            <div className={`${styles.sectionHeader} scroll-reveal`}>
              <h2 className={styles.sectionTitle}>
                App <span className={styles.textGradient}>Capabilities</span>
              </h2>
              <p className={styles.sectionSubtitle}>
                Advanced features for seamless healthcare management with
                merchant controls and admin oversight
              </p>
            </div>

            <div className={styles.platformGrid}>
              {mobileCapabilities.map((feature, index) => (
                <div
                  key={index}
                  className={`${styles.platformCard} scroll-reveal`}
                >
                  <div className={styles.platformIcon}>{feature.icon}</div>
                  <h4 className={styles.platformTitle}>{feature.title}</h4>
                  <p className={styles.platformDescription}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* App Download CTA */}
        <section className={styles.ctaSection}>
          <div className={styles.container}>
            <div className={`${styles.ctaContent} scroll-reveal`}>
              <div className={styles.ctaIcon}>
                <FaMobileAlt />
              </div>
              <h2 className={styles.ctaTitle}>
                Ready for
                <span className={styles.ctaAccent}> Mobile Healthcare</span>?
              </h2>
              <p className={styles.ctaSubtitle}>
                Join thousands using our mobile app for complete healthcare
                management. Connect with doctors, earn fee cashback, and access
                quality healthcare anytime.
              </p>
              <div className={styles.ctaActions}>
                <DownloadModal variant="button" className={styles.btnWhite} />
                <a
                  href={ROUTES.EXTERNAL.EMERGENCY_HOTLINE}
                  className={styles.btnOutline}
                >
                  <i className="fas fa-phone"></i>
                  <span>{ROUTES.EXTERNAL.EMERGENCY_DISPLAY}</span>
                </a>
              </div>
              <div className={styles.ctaNote}>
                <FaCheckCircle />
                <small>
                  Free Download • Transparent Fees • Secure Platform
                </small>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default FeaturesPage;
